import { useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";

import {
  responderThunk,
  adjuntarMediaThunk,
  eliminarMediaThunk,
} from "../../store/conversacion/conversacion.thunks";

const TIPO_COLORS = {
  PROPOSITO: { bg: "bg-primary-subtle", icon: "text-primary", badge: "Propósito de la conversación" },
  UTILIZAR: { bg: "bg-warning-subtle", icon: "text-warning", badge: "¿Cómo la utilizará el Mentor?" },
};

const ConfiguracionCard = ({
  config,
  respuestas,
  sesionId,
  miTipoAutor,
  isMentor,
  isEstudiante,
}) => {
  const dispatch = useDispatch();
  const { respondiendo } = useSelector((state) => state.conversacion);

  const [textoMentor, setTextoMentor] = useState("");
  const [textoEstudiante, setTextoEstudiante] = useState("");
  const [archivos, setArchivos] = useState([]);
  const [expanded, setExpanded] = useState(true);

  const tipoColor = TIPO_COLORS[config.tipo] || TIPO_COLORS.PROPOSITO;

  // Encontrar respuestas existentes para esta configuración
  const respuestaMentor = respuestas.find(
    (r) =>
      r.configuracion_conversacion === config.id &&
      r.tipo_autor === "MENTOR",
  );
  const respuestaEstudiante = respuestas.find(
    (r) =>
      r.configuracion_conversacion === config.id &&
      r.tipo_autor === "ESTUDIANTE",
  );

  const handleResponder = useCallback(
    async (texto, tipoAutor) => {
      if (!texto || !texto.trim()) {
        toast.warning("Escribe una respuesta antes de guardar.", {
          position: "top-center",
        });
        return;
      }
      const result = await dispatch(
        responderThunk({
          sesion_id: sesionId,
          configuracion_id: config.id,
          texto: texto.trim(),
          tipo_autor: tipoAutor,
        }),
      );
      if (result.type.endsWith("/fulfilled")) {
        toast.success("Respuesta guardada.", { position: "top-center" });
      }
    },
    [sesionId, config.id, dispatch],
  );

  const handleAdjuntarArchivos = useCallback(
    async (respuestaId) => {
      if (archivos.length === 0) return;
      const formData = new FormData();
      formData.append("respuesta_id", respuestaId);
      archivos.forEach((f) => formData.append("archivos", f.file));
      archivos.forEach((f) => formData.append("tipos_archivo", f.tipo));

      const result = await dispatch(adjuntarMediaThunk(formData));
      if (result.type.endsWith("/fulfilled")) {
        toast.success("Archivos adjuntados.", { position: "top-center" });
        setArchivos([]);
      }
    },
    [archivos, dispatch],
  );

  const handleEliminarArchivo = useCallback(
    async (mediaId) => {
      const result = await dispatch(eliminarMediaThunk(mediaId));
      if (result.type.endsWith("/fulfilled")) {
        toast.success("Archivo eliminado.", { position: "top-center" });
      }
    },
    [dispatch],
  );

  const handleFileChange = useCallback(
    (e, tipoDefault = "documento") => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;
      const nuevos = files.map((f) => {
        let tipo = tipoDefault;
        if (f.type.startsWith("image/")) tipo = "imagen";
        else if (f.type.startsWith("video/")) tipo = "video";
        else if (f.type.startsWith("audio/")) tipo = "audio";
        return { file: f, tipo };
      });
      setArchivos((prev) => [...prev, ...nuevos]);
      e.target.value = "";
    },
    [],
  );

  const quitarArchivo = useCallback((idx) => {
    setArchivos((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  return (
    <div className="configuracion-card card">
      {/* Cabecera colapsable */}
      <div
        className="configuracion-card__header d-flex align-items-center gap-3 p-3"
        onClick={() => setExpanded(!expanded)}
        role="button"
        style={{ cursor: "pointer", userSelect: "none" }}
      >
        <div
          className={`configuracion-icon-wrapper ${tipoColor.bg} d-flex align-items-center justify-content-center flex-shrink-0`}
          style={{ width: 42, height: 42, borderRadius: 12 }}
        >
          <Icon
            icon={config.tipo === "PROPOSITO" ? "mdi:target" : "mdi:lightbulb-on-outline"}
            width="20"
            className={tipoColor.icon}
          />
        </div>
        <div className="flex-grow-1 min-w-0">
          <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
            <span className={`badge ${tipoColor.bg} ${tipoColor.icon} fw-medium`} style={{ fontSize: "0.75rem" }}>
              {config.tipo === "PROPOSITO" ? "Propósito" : "Utilizar"}
            </span>
            <small className="text-secondary">{config.talento_nombre}</small>
          </div>
          <p className="configuracion-card__frase mb-0">
            {config.frase}
          </p>
        </div>
        <div className="d-flex align-items-center gap-2 flex-shrink-0">
          {config.tiene_respuesta_mentor && (
            <span className="badge bg-success-subtle text-success border border-success border-opacity-25" style={{ fontSize: "0.7rem" }}>
              <Icon icon="mdi:check-circle" width="12" className="me-1" />Mentor
            </span>
          )}
          {config.tiene_respuesta_estudiante && (
            <span className="badge bg-info-subtle text-info border border-info border-opacity-25" style={{ fontSize: "0.7rem" }}>
              <Icon icon="mdi:check-circle" width="12" className="me-1" />Estudiante
            </span>
          )}
          <Icon
            icon={expanded ? "mdi:chevron-up" : "mdi:chevron-down"}
            width="20"
            className="text-muted"
          />
        </div>
      </div>

      {/* Cuerpo expandible */}
      {expanded && (
        <div className="configuracion-card__body px-3 pb-3 pt-0">
          <hr className="my-0 mb-3" />

          <div className="row g-4">
            {/* Respuesta del Mentor */}
            <div className="col-md-6">
              <div className="d-flex align-items-center gap-2 mb-3">
                <Icon icon="mdi:account-tie" width="18" className="text-primary" />
                <h6 className="mb-0" style={{ fontSize: "0.9rem" }}>Mentor</h6>
                {respuestaMentor && (
                  <span className="badge bg-success-subtle text-success" style={{ fontSize: "0.65rem" }}>Respondido</span>
                )}
              </div>
              {respuestaMentor ? (
                <div>
                  {respuestaMentor.texto && (
                    <div className="respuesta-bubble respuesta-bubble--mentor p-3 rounded-3 mb-3">
                      <p className="mb-2" style={{ fontSize: "0.875rem" }}>{respuestaMentor.texto}</p>
                      <small className="text-secondary">— {respuestaMentor.usuario_nombre}</small>
                    </div>
                  )}
                  {respuestaMentor.archivos_multimedia?.length > 0 && (
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      {respuestaMentor.archivos_multimedia.map((m) => (
                        <div key={m.id} className="media-chip">
                          <Icon icon={m.tipo_archivo === "imagen" ? "mdi:image" : m.tipo_archivo === "video" ? "mdi:video" : m.tipo_archivo === "audio" ? "mdi:microphone" : "mdi:file-document"} width="14" />
                          <a href={m.archivo_url} target="_blank" rel="noopener noreferrer" className="ms-1">
                            {m.nombre_original || m.tipo_archivo_display}
                          </a>
                          {isMentor && (
                            <button className="media-chip__remove" onClick={() => handleEliminarArchivo(m.id)} title="Eliminar">
                              <Icon icon="mdi:close-circle" width="14" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {isMentor && (
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <input type="file" multiple onChange={(e) => handleFileChange(e)} className="form-control form-control-sm" style={{ maxWidth: 200 }} />
                      <button className="btn btn-sm btn-outline-primary" onClick={() => handleAdjuntarArchivos(respuestaMentor.id)} disabled={archivos.length === 0}>
                        <Icon icon="mdi:paperclip" width="14" className="me-1" />Adjuntar
                      </button>
                    </div>
                  )}
                  {archivos.length > 0 && (
                    <div className="d-flex flex-wrap gap-1 mt-2">
                      {archivos.map((a, i) => (
                        <span key={i} className="badge bg-body-secondary text-body">
                          {a.file.name}
                          <button className="btn-close ms-1" style={{ fontSize: "0.45rem" }} onClick={() => quitarArchivo(i)} />
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ) : isMentor ? (
                <div>
                  <textarea className="form-control mb-2" rows={3} placeholder="Escribe tu valoración sobre este propósito..." value={textoMentor} onChange={(e) => setTextoMentor(e.target.value)} disabled={respondiendo} style={{ fontSize: "0.875rem" }} />
                  <button className="btn btn-sm btn-primary" onClick={() => handleResponder(textoMentor, "MENTOR")} disabled={respondiendo || !textoMentor.trim()}>
                    <Icon icon="mdi:send" width="14" className="me-1" />
                    {respondiendo ? "Guardando..." : "Guardar"}
                  </button>
                </div>
              ) : (
                <p className="text-muted fst-italic" style={{ fontSize: "0.85rem" }}>El mentor aún no ha respondido a esta sección.</p>
              )}
            </div>

            {/* Respuesta del Estudiante */}
            <div className="col-md-6">
              <div className="d-flex align-items-center gap-2 mb-3">
                <Icon icon="mdi:account-school" width="18" className="text-info" />
                <h6 className="mb-0" style={{ fontSize: "0.9rem" }}>Estudiante</h6>
                {respuestaEstudiante && (
                  <span className="badge bg-info-subtle text-info" style={{ fontSize: "0.65rem" }}>Respondido</span>
                )}
              </div>
              {respuestaEstudiante ? (
                <div>
                  {respuestaEstudiante.texto && (
                    <div className="respuesta-bubble respuesta-bubble--estudiante p-3 rounded-3 mb-3">
                      <p className="mb-2" style={{ fontSize: "0.875rem" }}>{respuestaEstudiante.texto}</p>
                      <small className="text-secondary">— {respuestaEstudiante.usuario_nombre}</small>
                    </div>
                  )}
                  {respuestaEstudiante.archivos_multimedia?.length > 0 && (
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      {respuestaEstudiante.archivos_multimedia.map((m) => (
                        <div key={m.id} className="media-chip">
                          <Icon icon={m.tipo_archivo === "imagen" ? "mdi:image" : m.tipo_archivo === "video" ? "mdi:video" : m.tipo_archivo === "audio" ? "mdi:microphone" : "mdi:file-document"} width="14" />
                          <a href={m.archivo_url} target="_blank" rel="noopener noreferrer" className="ms-1">
                            {m.nombre_original || m.tipo_archivo_display}
                          </a>
                          {isEstudiante && (
                            <button className="media-chip__remove" onClick={() => handleEliminarArchivo(m.id)} title="Eliminar">
                              <Icon icon="mdi:close-circle" width="14" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {isEstudiante && (
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <input type="file" multiple onChange={(e) => handleFileChange(e)} className="form-control form-control-sm" style={{ maxWidth: 200 }} />
                      <button className="btn btn-sm btn-outline-info" onClick={() => handleAdjuntarArchivos(respuestaEstudiante.id)} disabled={archivos.length === 0}>
                        <Icon icon="mdi:paperclip" width="14" className="me-1" />Adjuntar
                      </button>
                    </div>
                  )}
                  {archivos.length > 0 && (
                    <div className="d-flex flex-wrap gap-1 mt-2">
                      {archivos.map((a, i) => (
                        <span key={i} className="badge bg-body-secondary text-body">
                          {a.file.name}
                          <button className="btn-close ms-1" style={{ fontSize: "0.45rem" }} onClick={() => quitarArchivo(i)} />
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ) : isEstudiante ? (
                <div>
                  <textarea className="form-control mb-2" rows={3} placeholder="Comparte tu punto de vista sobre esta pregunta..." value={textoEstudiante} onChange={(e) => setTextoEstudiante(e.target.value)} disabled={respondiendo} style={{ fontSize: "0.875rem" }} />
                  <button className="btn btn-sm btn-info text-white" onClick={() => handleResponder(textoEstudiante, "ESTUDIANTE")} disabled={respondiendo || !textoEstudiante.trim()}>
                    <Icon icon="mdi:send" width="14" className="me-1" />
                    {respondiendo ? "Guardando..." : "Guardar"}
                  </button>
                </div>
              ) : (
                <p className="text-muted fst-italic" style={{ fontSize: "0.85rem" }}>El estudiante aún no ha respondido a esta sección.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfiguracionCard;
